import _ from 'lodash';
import { type BoardSource } from '@gorilla/portal-shared/src/schemas';
import { type PortalSettings } from '@gorilla/portal-shared/src/schemas';
import { Flex, Box, Text, Icon, IconButton, Label, Divider, List, ListItem, Tooltip } from '@vibe/core';
import { Board, Item, Person, Team, Delete, AddSmall, MoveArrowRight, Settings, BoardPrivate } from '@vibe/icons';
import { boardSourceDescriptorsByType } from './board-sources/index';
import { BoardSourceIndicator } from './board-sources/index';
import { getBoardSourceIdsInUse } from '../hooks/use-portal';

type BoardSourceRowProps = {
  boardSource: BoardSource;
  isInUse: boolean;
  onSelectClick: () => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
  supportSelectButton: boolean;
  supportDeleteButton: boolean;
  supportEditButton: boolean;
};

function BoardSourceRow({
  boardSource,
  isInUse,
  onSelectClick,
  onDeleteClick,
  onEditClick,
  supportSelectButton,
  supportDeleteButton,
  supportEditButton,
}: BoardSourceRowProps) {
  const descriptor = boardSourceDescriptorsByType[boardSource.type];

  return (
    <ListItem size="medium" className="portalmanager__content__listitem--noclick portalmanager__content__listitem">
      <Flex style={{ width: '100%' }}>
        <Box style={{ flex: 1 }}>
          <Flex>
            <Flex gap={8}>
              <Tooltip content={descriptor.description} position="right">
                <Text type="text2" align="start" color="primary" element="p">
                  <BoardSourceIndicator boardSourceType={boardSource.type} hideHelpLink={true}>
                    {boardSource.name}
                  </BoardSourceIndicator>
                </Text>
              </Tooltip>
              {/* 
              <Tooltip content={`${descriptor.description}. ${descriptor.example}`} position="right">
                <Label text={descriptor.name} kind="line" color="dark" size="medium" />
              </Tooltip>
              */}
            </Flex>
          </Flex>
        </Box>

        <Box style={{ marginLeft: 'auto' }}>
          <Flex gap={0}>
            {supportSelectButton ? (
              <IconButton
                icon={AddSmall}
                kind="primary"
                size="xs"
                color="primary"
                onClick={() => {
                  onSelectClick();
                }}
                className="custom__deletepagebutton"
              />
            ) : null}
            {supportDeleteButton ? (
              <IconButton
                icon={Delete}
                kind="tertiary"
                size="small"
                color="primary"
                disabled={isInUse}
                onClick={() => {
                  onDeleteClick();
                }}
                className="custom__deletepagebutton"
                tooltipContent={isInUse ? 'Board source is used in a section' : undefined}
                tooltipProps={{
                  position: 'left',
                  showOnDialogEnter: false,
                }}
              />
            ) : null}
            {supportEditButton ? (
              <IconButton
                icon={Settings}
                kind="tertiary"
                size="small"
                color="primary"
                onClick={() => {
                  onEditClick();
                }}
                className="custom__deletepagebutton"
              />
            ) : null}
          </Flex>
        </Box>
      </Flex>
    </ListItem>
  );
}

type BoardSourceListProps = {
  portalSettings: PortalSettings;
  onSelectClick?: (boardSource: BoardSource) => void;
  onDeleteClick?: (boardSource: BoardSource) => void;
  onEditClick?: (boardSource: BoardSource) => void;
  supportSelectButton?: boolean;
  supportDeleteButton?: boolean;
  supportEditButton?: boolean;
};

export function BoardSourceList({
  portalSettings,
  onSelectClick,
  onDeleteClick,
  onEditClick,
  supportSelectButton = false,
  supportDeleteButton = false,
  supportEditButton = false,
}: BoardSourceListProps) {
  const { boardSources } = portalSettings;
  const boardSourceIdsInUse = getBoardSourceIdsInUse(portalSettings);

  return (
    <List
      style={{
        overflow: 'hidden',
      }}
    >
      {boardSources.map((boardSource, index) => {
        const isInUse = boardSourceIdsInUse.includes(boardSource.id);
        return (
          <Box key={boardSource.id}>
            <BoardSourceRow
              boardSource={boardSource}
              isInUse={!!isInUse}
              supportSelectButton={supportSelectButton}
              supportDeleteButton={supportDeleteButton}
              supportEditButton={supportEditButton}
              onDeleteClick={() => {
                onDeleteClick?.(boardSource);
              }}
              onSelectClick={() => {
                onSelectClick?.(boardSource);
              }}
              onEditClick={() => {
                onEditClick?.(boardSource);
              }}
            />
            {index !== boardSources.length - 1 && <Divider withoutMargin />}
          </Box>
        );
      })}
    </List>
  );
}
